// utils/useAuthGate.ts
//
// The home page is reachable while logged out, so any action that needs an
// account has to send the visitor to /login instead of half-working.

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export const useAuthGate = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Wraps an action so it only runs for signed-in users. Everyone else is sent
   * to /login, with the current path remembered so they can be returned here.
   */
  const requireAuth = useCallback(
    (action: () => void) =>
      () => {
        // Still resolving the session — don't bounce a user who is signed in.
        if (isLoading) return;

        if (!isAuthenticated) {
          navigate("/login", {
            replace: false,
            state: { from: location.pathname + location.search },
          });
          return;
        }

        action();
      },
    [isAuthenticated, isLoading, navigate, location.pathname, location.search],
  );

  return { isAuthenticated, isLoading, requireAuth };
};

export default useAuthGate;
